import { api } from '@/lib/api';
import type {
  Transmitter,
  TransmittersFilters,
  TransmittersResponse,
  PopulateResponse,
  TransmitterOptions,
  TransmitterFormData,
  HardwareOption,
  TransmitterConfig,
  ChangelogEntry,
  V4Transmitter,
  V4TransmittersResponse,
} from './types';

// =============================================================================
// V4 Mapper (PostgreSQL camelCase -> legacy Transmitter shape)
// =============================================================================

function mapV4ToTransmitter(t: V4Transmitter): Transmitter {
  return {
    _id: t.mongoTransmitterId,
    dev_id: t.devId,
    name: t.name || '',
    customid: t.customId || undefined,
    xbee_SL: t.xbeeSl || undefined,
    organisation: t.accountName
      ? { _id: t.mongoOrganisationId || '', name: t.accountName, accountId: t.accountId || undefined }
      : null,
    site: t.siteName
      ? { _id: t.mongoSiteId || '', name: t.siteName }
      : null,
    location: t.locationName
      ? { _id: t.mongoLocationId || '', name: t.locationName }
      : null,
    streams: Array.from({ length: t.streamCount }, (_, i) => String(i)),
    activated: t.activated,
    active_flag: t.activeFlag,
    created_at: t.createdAt || undefined,
    installed_at: t.installedAt || undefined,
    updated_at: t.updatedAt || undefined,
    lastpacket_at: t.lastpacketAt || undefined,
    lastdata_at: t.lastdataAt || undefined,
    battery_voltage: t.batteryVoltage ? Number(t.batteryVoltage) : undefined,
    battery_percentage: t.batteryPercentage ? Number(t.batteryPercentage) : null,
    power_external: t.powerExternal,
    rssi: t.rssi ?? undefined,
    current_firmware_name: t.currentFirmwareName || undefined,
    ICCID: t.iccid || undefined,
    IMEI: t.imei || undefined,
    simprovider: t.simprovider || undefined,
    description: t.description || undefined,
    transmission_interval_seconds: t.transmissionIntervalSeconds ?? undefined,
    image: t.mongoImageId && t.mongoOrganisationId
      ? { _id: t.mongoImageId, organisation: t.mongoOrganisationId, extension: '' }
      : null,
    image2: t.mongoImage2Id && t.mongoOrganisationId
      ? { _id: t.mongoImage2Id, organisation: t.mongoOrganisationId, extension: '' }
      : null,
  };
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get transmitters with filtering, pagination, and sorting.
 * Uses V4 PostgreSQL endpoint (single request returns data + total).
 */
export async function getTransmitters(
  filters: TransmittersFilters = {},
): Promise<TransmittersResponse> {
  const params: Record<string, string | number> = {};

  if (filters.generalSearch) {
    params.search = filters.generalSearch;
  }

  params.pageNumber = filters.pageNumber ?? 0;
  params.pageLength = filters.pageLength ?? 50;

  if (filters.sortKey) {
    params.sortKey = filters.sortKey;
  }
  if (filters.sortDirection !== undefined) {
    params.sortDirection = filters.sortDirection;
  }

  if (filters.type) {
    params.type = filters.type;
  }

  if (filters.hasImage) {
    params.hasImage = filters.hasImage;
  }

  if (filters.accountIdIn && filters.accountIdIn.length > 0) {
    params.accountIdIn = JSON.stringify(filters.accountIdIn);
  }

  const response = await api.get<V4TransmittersResponse>('/v4/transmitters', params);

  return {
    transmitters: (response.transmitters || []).map(mapV4ToTransmitter),
    total: response.total || 0,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
  };
}

/**
 * Get all account names for searchable dropdown filters.
 * Paginates through all pages since API caps at 100 per page.
 */
export async function getAllAccountNames(): Promise<{ id: string; name: string }[]> {
  const all: { id: string; name: string }[] = [];
  let pageNumber = 0;
  const PAGE_SIZE = 100;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = await api.get<{ accounts: { id: string; name: string }[]; total: number }>(
      '/v4/accounts',
      { pageNumber, pageLength: PAGE_SIZE, sortKey: 'name', sortDirection: 1 },
    );
    const accounts = response.accounts || [];
    for (const a of accounts) {
      all.push({ id: a.id, name: a.name });
    }
    if (accounts.length < PAGE_SIZE) break;
    pageNumber++;
  }

  return all;
}

/**
 * Get a single transmitter by ID
 */
export async function getTransmitterById(id: string): Promise<Transmitter> {
  const response = await api.get<Transmitter[]>(`/v4/legacy/transmitters/${id}`);
  // V3 returns array for single item via mongo_server
  if (Array.isArray(response)) {
    if (response.length === 0) throw new Error('Transmitter not found');
    return response[0];
  }
  return response as Transmitter;
}

// =============================================================================
// V2 API Functions (populate, options, update)
// =============================================================================

/**
 * Get transmitter with all dropdown options via V2 populate endpoint.
 * Admin and non-admin paths return different option shapes.
 */
export async function getTransmitterPopulate(
  id: string,
  isAdmin: boolean,
  deviceType: 'transmitters' | 'gateways' = 'transmitters',
): Promise<PopulateResponse> {
  const basePath = isAdmin
    ? `/api/v4/legacy/v2/superadmin/transmitters/${id}/populate`
    : `/api/v4/legacy/v2/transmitters/${id}/populate`;
  return api.get<PopulateResponse>(basePath, { type: deviceType });
}

/**
 * Reload dropdown options when organisation changes (admin-only).
 */
export async function getTransmitterOptions(
  orgId: string,
  isAdmin: boolean,
  deviceType: 'transmitters' | 'gateways' = 'transmitters',
): Promise<{ options: TransmitterOptions }> {
  const basePath = isAdmin
    ? '/api/v4/legacy/v2/superadmin/transmitters/new-options'
    : '/api/v4/legacy/v2/transmitters/new-options';
  return api.get<{ options: TransmitterOptions }>(basePath, {
    type: deviceType,
    organisation: orgId,
  });
}

/**
 * Update transmitter via V2 PUT endpoint.
 * Sends only changed fields. V2 handles audit trail server-side.
 */
export async function updateTransmitter(
  id: string,
  data: Partial<TransmitterFormData>,
  isAdmin: boolean,
): Promise<unknown> {
  const basePath = isAdmin
    ? `/api/v4/legacy/v2/superadmin/transmitters/${id}`
    : `/api/v4/legacy/v2/transmitters/${id}`;
  return api.put(basePath, data);
}

/**
 * Get hardware options (base, module, or connectivity).
 * Reads from /v4/hardwares (web_api_server passthrough to the rest_server
 * hardware catalogue). Returns the same `{ hardwares: HardwareOption[] }`
 * envelope.
 */
export async function getHardwareOptions(
  type: 'base' | 'module' | 'connectivity',
): Promise<HardwareOption[]> {
  const response = await api.get<{ hardwares: HardwareOption[] }>('/v4/hardwares', {
    type,
    pageLength: 100,
  });
  return response.hardwares ?? [];
}

// =============================================================================
// OTA Operations
// =============================================================================

export interface PushFirmwarePayload {
  firmware: string;
  max_ota_attempts?: number;
  ota_delay_minutes?: number;
  to_update: true;
  ota_firmware_attempts: 0;
  last_ota_attempt: null;
}

export async function pushFirmware(
  id: string,
  data: PushFirmwarePayload,
): Promise<unknown> {
  return api.put(`/api/v4/legacy/v2/superadmin/transmitters/${id}`, data);
}

export interface PushConfigsPayload {
  configs: TransmitterConfig[];
  remote_config_enabled: true;
  ota_config_attempts: 0;
  last_config_attempt: null;
  to_config: true;
  ota_delay_minutes?: number;
}

export interface PushConfigsResponse {
  transmitter: unknown;
  otaWarning?: string;
}

export interface TransmitterConfigsReadResponse {
  devEui: string;
  transmitterId: string;
  deviceType?: string | null;
  configs?: TransmitterConfig[] | Record<string, unknown> | null;
  confirmedConfigs?: Record<string, unknown> | null;
  confirmedConfigsAt?: string | null;
  configJson?: Record<string, unknown> | string | null;
  toConfig?: boolean;
  ota?: {
    state?: string | null;
    batchId?: string | null;
    attempts?: number | null;
    lastAttempt?: string | null;
  };
}

export async function getTransmitterConfigs(
  devEui: string,
): Promise<TransmitterConfigsReadResponse> {
  return api.get<TransmitterConfigsReadResponse>(`/v4/configs/${encodeURIComponent(devEui)}`);
}

export async function pushConfigs(
  id: string,
  data: PushConfigsPayload,
): Promise<PushConfigsResponse> {
  return api.put<PushConfigsResponse>(`/api/v4/legacy/v2/transmitters/${id}`, data);
}

// =============================================================================
// Destructive Actions
// =============================================================================

export async function deleteTransmitter(id: string): Promise<unknown> {
  return api.delete(`/v4/legacy/transmitters/${id}`);
}

export async function resetSim(iccid: string): Promise<unknown> {
  return api.put(`/v4/legacy/sims/${iccid}`, { status: 'resetting' });
}

// =============================================================================
// Changelog
// =============================================================================

export async function getChangelog(id: string): Promise<ChangelogEntry[]> {
  return api.get<ChangelogEntry[]>(`/api/v4/legacy/v2/transmitters/${id}/changelog`);
}

// =============================================================================
// Image Upload
// =============================================================================

export async function uploadTransmitterImage(
  transmitterId: string,
  file: File,
  slot: 1 | 2,
  isAdmin: boolean,
  organisationId?: string,
): Promise<unknown> {
  const formData = new FormData();
  formData.append('file', file);
  const uploadPath = organisationId
    ? `/v4/legacy/v2/files/upload?organisation=${organisationId}`
    : '/v4/legacy/v2/files/upload';
  const uploadResponse = await api.upload<{ _id: string; organisation: string; extension: string }>(
    uploadPath,
    formData,
  );
  const imageField = slot === 1 ? 'image' : 'image2';
  const basePath = isAdmin
    ? `/api/v4/legacy/v2/superadmin/transmitters/${transmitterId}`
    : `/api/v4/legacy/v2/transmitters/${transmitterId}`;
  return api.put(basePath, { [imageField]: uploadResponse });
}

export async function deleteTransmitterImage(
  transmitterId: string,
  fileId: string,
  slot: 1 | 2,
  isAdmin: boolean,
): Promise<unknown> {
  await api.delete(`/v4/legacy/v2/files/${fileId}`);
  const imageField = slot === 1 ? 'image' : 'image2';
  const basePath = isAdmin
    ? `/api/v4/legacy/v2/superadmin/transmitters/${transmitterId}`
    : `/api/v4/legacy/v2/transmitters/${transmitterId}`;
  return api.put(basePath, { [imageField]: null });
}

// =============================================================================
// Create Location
// =============================================================================

export async function createLocation(
  data: { name: string; site?: string; organisation?: string },
): Promise<{ _id: string; name: string }> {
  return api.post<{ _id: string; name: string }>('/v4/legacy/locations', data);
}
